/** Report periods, units and the CSV: plain functions, so they're easy to test. */
import type { Report } from "./api.ts";
import { formatNumber } from "./format.ts";

export type PeriodId = "7d" | "30d" | "month" | "last-month" | "season" | "custom";
export const PERIODS: Array<[PeriodId, string]> = [
  ["7d", "Last 7 days"],
  ["30d", "Last 30 days"],
  ["month", "This month"],
  ["last-month", "Last month"],
  ["season", "This season"],
  ["custom", "Pick dates"],
];

export const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

/** From and to dates (inclusive) for a period, in the farm's local dates. */
export function periodDates(id: PeriodId, today: Date, seasonStartMonth = 7): { from: string; to: string } {
  const d = (y: number, m: number, day: number) => iso(new Date(y, m, day));
  const y = today.getFullYear();
  const m = today.getMonth();
  switch (id) {
    case "7d":
      return { from: d(y, m, today.getDate() - 6), to: iso(today) };
    case "30d":
      return { from: d(y, m, today.getDate() - 29), to: iso(today) };
    case "month":
      return { from: d(y, m, 1), to: iso(today) };
    case "last-month":
      return { from: d(y, m - 1, 1), to: d(y, m, 0) };
    default: {
      const start = seasonStartMonth - 1;
      return { from: d(m >= start ? y : y - 1, start, 1), to: iso(today) };
    }
  }
}

/** Litres as kL, or ML when it's a lot. */
export function water(litres: number): { value: string; unit: string } {
  return litres >= 10_000_000 ? { value: formatNumber(litres / 1e6), unit: "ML" } : { value: formatNumber(litres / 1000), unit: "kL" };
}

/** One row per item per day, for a spreadsheet. */
export function reportCsv(r: Report): string {
  const rows: Array<Array<string | number>> = [["Date", "Type", "Device", "Item", "Amount", "Unit", "Cost (AUD)"]];
  const round = (v: number) => Math.round(v * 1000) / 1000;
  r.days.forEach((day, i) => {
    for (const l of r.water) rows.push([day, "Water", l.device, l.label, round(l.byDay[i]! / 1000), "kL", ""]);
    for (const l of r.run) rows.push([day, l.output ? "Run hours" : "Input on", l.device, l.label, round(l.byDay[i]!), "h", ""]);
    for (const l of r.energy) rows.push([day, "Energy", l.device, l.label, round(l.byDay[i]!), "kWh", r.tariffCents ? round((l.byDay[i]! * r.tariffCents) / 100) : ""]);
  });
  const cell = (v: string | number) => (typeof v === "number" ? String(v) : /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
  return rows.map((row) => row.map(cell).join(",")).join("\r\n");
}

